/*
 * switch-svg-quality.js
 * Keep SVGs vector-crisp on the storefront across two rendering paths.
 *
 *   Pass 1 — <img src="*.svg?width=N">
 *   Horizon's _icon, _image, header-logo, and similar blocks pipe SVG sources
 *   through the image_url Liquid filter, which appends ?width=/height= query
 *   params and routes the asset through Shopify's Image CDN. The CDN
 *   rasterises the SVG to a fixed-pixel PNG-equivalent at the requested
 *   dimensions, so on retina (DPR ≥ 2) the result is upscaled and blurry.
 *   Stripping the resize params restores the original vector source.
 *
 *   Pass 2 — <object type="image/svg+xml">
 *   The sl-scroll-carousel meta-cards (and any other consumer that uses
 *   <object> for animated SVGs) hit a different mechanism: when the inner
 *   <svg> root carries explicit width/height attributes (Illustrator/Figma
 *   exports often add these), the browser treats the SVG as a fixed-resolution
 *   replaced element and rasterises it at those dims, then scales the bitmap
 *   to fit the container. Removing the width/height attrs from the inner
 *   document forces pure vector layout at any container size and DPR.
 *
 *   Both passes run on initial DOM, then a MutationObserver picks up any
 *   late-injected nodes (carousel track clones, cart drawer, predictive
 *   search results, dynamic section renders).
 *
 *   Safety: only edits inside obj.contentDocument (a separate document tree),
 *   never mutates the host DOM in a way that could feedback-loop with the
 *   carousel's own observers. removeAttribute is idempotent so re-runs are
 *   no-ops.
 */
(function () {
  'use strict';

  function stripImgPipelineParams(img) {
    const src = img.currentSrc || img.src;
    if (!src || !/\.svg(\?|$)/i.test(src)) return;
    let url;
    try { url = new URL(src, window.location.origin); } catch (_) { return; }
    const params = url.searchParams;
    let changed = false;
    ['width', 'height', 'crop'].forEach((p) => {
      if (params.has(p)) { params.delete(p); changed = true; }
    });
    if (changed && img.src !== url.toString()) {
      img.src = url.toString();
    }
  }

  function stripObjectIntrinsicSize(obj) {
    const apply = () => {
      try {
        const doc = obj.contentDocument;
        const root = doc && doc.documentElement;
        if (!root || root.tagName.toLowerCase() !== 'svg') return;
        if (root.hasAttribute('width')) root.removeAttribute('width');
        if (root.hasAttribute('height')) root.removeAttribute('height');
      } catch (_) { /* cross-origin guard */ }
    };
    apply();
    obj.addEventListener('load', apply);
  }

  function isSvgObject(node) {
    if (!node || node.tagName !== 'OBJECT') return false;
    const type = node.getAttribute('type');
    if (type && type.toLowerCase() === 'image/svg+xml') return true;
    const data = node.getAttribute('data') || '';
    return /\.svg(\?|$)/i.test(data);
  }

  function processSubtree(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('img').forEach(stripImgPipelineParams);
    root.querySelectorAll('object').forEach((obj) => {
      if (isSvgObject(obj)) stripObjectIntrinsicSize(obj);
    });
  }

  function init() {
    processSubtree(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') stripImgPipelineParams(node);
          else if (isSvgObject(node)) stripObjectIntrinsicSize(node);
          processSubtree(node);
        }
      }
    });
    observer.observe(document.body, { subtree: true, childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
